/**
 * Pure relative-time bucket selection (docs/102-RECENTS-SEE-ALL-AND-RELATIVE-TIME.md v0):
 * turns a "when" timestamp into a small, localizable label like "opened 2h ago" for the
 * My Library "Recently opened" rows (both the home section and the `/recents` See-all
 * page). The clock is **injected** (`nowMs`) — no `Date.now()` in core, per repo rule; the
 * caller captures "now" once (on render / `useFocusEffect`) and passes it in.
 *
 * Only bucket *selection* lives here — the actual localized string (unit word, singular
 * vs plural, absolute-date formatting) is the UI's job (`src/ui/RecentRow.tsx`), since
 * that depends on the active content language / `Intl`, which this framework-free module
 * has no access to.
 */

/** Which bucket a relative time falls into. `"date"` means "beyond a week" — the caller
 * should format `thenMs` as an absolute short date instead of a relative count. */
export type RelativeTimeUnit = "now" | "minutes" | "hours" | "days" | "date";

export interface RelativeTime {
  unit: RelativeTimeUnit;
  /** Elapsed count of `unit`s. Always `0` for `"now"`/`"date"` (unused there). */
  count: number;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

/**
 * Bucket-select `thenMs` relative to `nowMs`. Boundaries (inclusive of the lower bucket):
 * `< 1m` → `"now"`; `< 1h` → `"minutes"`; `< 1d` → `"hours"`; `< 7d` → `"days"`; `>= 7d` →
 * `"date"`. A future timestamp, `0`, or a non-finite/`NaN` diff all fall back to `"now"`
 * (fail-safe: never shows a negative or garbage count).
 */
export function formatRelativeTime(nowMs: number, thenMs: number): RelativeTime {
  const diff = nowMs - thenMs;
  if (!Number.isFinite(diff) || diff < MINUTE_MS) return { unit: "now", count: 0 };
  if (diff < HOUR_MS) return { unit: "minutes", count: Math.floor(diff / MINUTE_MS) };
  if (diff < DAY_MS) return { unit: "hours", count: Math.floor(diff / HOUR_MS) };
  if (diff < WEEK_MS) return { unit: "days", count: Math.floor(diff / DAY_MS) };
  return { unit: "date", count: 0 };
}
